import { Form as FormikForm, Formik, FormikErrors, FormikProps, FormikTouched } from 'formik';
import React, { useEffect, useState } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { BackButton } from '../../../../../components/buttons/BackButton';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import Stepper from '../../../../../components/stepper/Stepper';
import { useToast } from '../../../../../context/ToastContext';
import {
  useCreateReceiptMutation,
  useGetReceiptQuery,
  useUpdateReceiptMutation
} from '../../../../../services/endpoints/finance/receipt';
import { getErrorMessage } from '../../../../../utils/utils';
import { FadeIn } from '../../../../animations/FadeIn';
import { ReceiptForm } from './ReceiptForm';

const steps = [{ name: 'Details', form: ReceiptForm }];

export interface FormValues {
  id?: number;
  course_id?: number;
  store_name: string;
  total: number;
  image: File;
}

// export interface FormValuesReceipt {
//   id?: number;
//   course_id: number;
//   store_name: string;
//   total: number;
//   image:File;
// }

export interface StepProps {
  errors: Partial<FormikErrors<FormValues>>;
  touched: Partial<FormikTouched<FormValues>>;
  values?: FormValues;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  readOnly?: boolean;
}

interface AddEditReceiptProps {
  editing?: boolean;
}

export const AddEditReceipt: React.FC<AddEditReceiptProps> = ({ editing }) => {
  const params = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const [currentStep, setCurrentStep] = useState(0);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [createReceipt] = useCreateReceiptMutation();
  const [updateReceipt] = useUpdateReceiptMutation();
  const { data, error, isLoading } = useGetReceiptQuery(Number(params.id!), {
    skip: !editing || !params.id
  });

  const [initialValues, setInitialValues] = useState<FormValues>({
    store_name: '',
    course_id: 0,
    total: 0,
    image: {} as File
  });

  const validationSchemas = [
    Yup.object({
      store_name: Yup.string().required('Store name is required'),
      total: Yup.number().required('Total is required')
    })
  ];

  useEffect(() => {
    if (editing && error && !isLoading) {
      addToast('Error while loading receipt', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, error, isLoading]);

  useEffect(() => {
    if (data && data.receipt) {
      const { receipt } = data;
      setInitialValues({
        id: receipt.id,
        course_id: receipt.course_id,
        store_name: receipt.store_name,
        total: receipt.total,
        image: receipt.image
      });
    }
  }, [data]);

  const handleSubmit = async (formValue: FormValues) => {
    try {
      const receiptData = {
        ...formValue,
        store_name: formValue.store_name ?? undefined,
        total: formValue.total ?? undefined
      };
      if (editing) {
        delete receiptData.course_id;
        const res = await updateReceipt(receiptData).unwrap();
        if (res && res.receipt) {
          addToast(`Receipt updated succesfully`, 'success');
          navigate(`/admin/finance/receipts/${data?.receipt.id}`);
        }
      } else {
        const res = await createReceipt(receiptData).unwrap();
        if (res && res.receipt) {
          navigate(`/admin/finance/receipts/${res.receipt.id}`);
          addToast('Receipt created succesfully', 'success');
        } else throw new Error('A problem happened while creating receipt');
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const handleNext = async (
    validateForm: () => Promise<FormikErrors<FormValues>>,
    setTouched: (touched: FormikTouched<FormValues>, shouldValidate?: boolean) => void,
    values: FormValues
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
    } else {
      setTouched({
        store_name: true,
        total: true
      });
    }
  };

  return (
    <FadeIn className="p-lg-4">
      <Stack
        gap={3}
        className="py-lg-0 px-4 py-2"
      >
        <div>
          <BackButton
            text={`Back to receipt${editing ? ' details' : 's'}`}
            color="#2B4E64"
            url={
              editing ? `/admin/finance/receipts/${data?.receipt.id}` : '/admin/finance/receipts'
            }
          />
          <h2
            className="d-none d-lg-block text-info fw-bold mb-0"
            style={{ fontSize: '2.25rem' }}
          >
            {editing ? 'Edit' : 'New'} Receipt
          </h2>
          {steps.length > 1 && (
            <Stepper
              steps={steps}
              currentStep={currentStep}
              editing={editing === true}
              onStepClick={(step) => setCurrentStep(step)}
            />
          )}
        </div>

        <div
          className="p-lg-5 p-3"
          style={{
            boxShadow: '0 4px 8px -2px rgba(16, 24, 40, .1),  0 4px 4px 0 rgba(0, 0, 0, .25)',
            border: '1px solid #EBEBEB',
            borderRadius: 10
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchemas[currentStep]}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              submitForm,
              isSubmitting,
              validateForm,
              touched,
              errors,
              setTouched,
              values,
              setFieldValue
            }: FormikProps<FormValues>) => (
              <FormikForm className="text-start">
                <div style={{ minHeight: '65vh' }}>
                  {currentStep < steps.length &&
                    React.createElement(steps[currentStep]?.form, {
                      errors,
                      touched,
                      values,
                      setFieldValue
                    })}
                </div>

                <Row className="justify-content-end mt-3">
                  <Col
                    xs={6}
                    lg={3}
                  >
                    {currentStep !== 0 && (
                      <SimpleButton
                        disabled={isSubmitting}
                        type="button"
                        onClick={() => setCurrentStep(currentStep - 1)}
                        className="w-100"
                        variant="outline-primary"
                      >
                        Back
                      </SimpleButton>
                    )}
                  </Col>

                  <Col
                    xs={6}
                    lg={3}
                  >
                    <SimpleButton
                      disabled={isSubmitting}
                      type="button"
                      onClick={
                        currentStep === steps.length - 1
                          ? submitForm
                          : () => handleNext(validateForm, setTouched, values)
                      }
                      className="w-100"
                      variant="primary"
                    >
                      {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                    </SimpleButton>
                  </Col>
                </Row>
              </FormikForm>
            )}
          </Formik>
        </div>
      </Stack>
    </FadeIn>
  );
};
